import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

/**
 * Keeps a multi-value field's RHF value an array.
 *
 * A row whose operator flips from a single-value one (Equals) to a multi-value
 * one (In) still holds the single-value shape — a `{ value }` object or a bare
 * string — and mounting Kendo's MultiSelect against that throws. The reset is
 * to `[]` rather than `[value]` on purpose: an Equals scalar is not a
 * meaningful In selection, and carrying it over would silently pre-select a
 * value the user never picked for this operator.
 *
 * Programmatic, so it must not dirty, touch, or validate the field.
 *
 * @param name    RHF field path.
 * @param value   The field's current value (the caller already has it from
 *                `useControlledField`, so this avoids a second subscription).
 * @param enabled Pass the caller's multi-value flag; a single-value field is
 *                left alone.
 */
export const useCoerceToArrayValue = (
  name: string,
  value: unknown,
  enabled = true,
): void => {
  const methods = useFormContext();

  useEffect(() => {
    if (!enabled) return;
    if (value == null || Array.isArray(value)) return;
    methods.setValue(name, [] as never, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [enabled, value, methods, name]);
};

export default useCoerceToArrayValue;
